import { Box, Stack, Theme } from '@mui/material';
import { FC, SyntheticEvent, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import TabPanel from '../tab-panel';
import TabBar from '../tab-bar';
import { TabPanel as TabPanelType } from './helpers';
import useUserControl from '@/hooks/use-user-control.hook';
import { useTabs } from '@/hooks/use-tabs.hook';

interface Props {
  panels: TabPanelType[];
  variant?: 'page' | 'drawer';
  onChange?: (tabPath: string) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  drawerTabBar: {
    position: 'sticky',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
    top: 0,
    zIndex: 10,
    margin: '0px -20px',
    padding: '10px 20px 0px',
  },
}));

const RouterTabs: FC<Props> = ({ panels, variant = 'page', onChange }) => {
  const styles = useStyles();
  const { tabPath, updateTabPath } = useTabs({ variant, onChange });
  const { checkLockedAndEmit } = useUserControl();

  const { PanelEl, tabIndex } = useMemo(() => {
    const matchingPanel = panels.find(({ path }) => path === tabPath);
    const panel =
      matchingPanel && !matchingPanel.disabled
        ? matchingPanel
        : panels.find(({ disabled }) => !disabled) || panels[0];

    return {
      PanelEl: panel.component,
      tabIndex: panels.findIndex(({ path }) => path === panel.path),
    };
  }, [panels, tabPath]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, tabIndex: number) => {
      if (checkLockedAndEmit(variant)) {
        return;
      }
      const targetPanel = panels[tabIndex];
      if (targetPanel?.path) {
        updateTabPath(targetPanel.path);
      }
    },
    [checkLockedAndEmit, panels, updateTabPath, variant]
  );

  const TabEl = (
    <TabBar
      items={panels.map(({ path, label, disabled, icon }) => ({
        key: path,
        label,
        disabled,
        icon,
      }))}
      tabIndex={tabIndex}
      onTabChange={handleChange}
    />
  );

  return (
    <Stack>
      {variant === 'page' ? TabEl : <Box className={styles.drawerTabBar}>{TabEl}</Box>}
      <TabPanel tabIndex={tabIndex}>{PanelEl}</TabPanel>
    </Stack>
  );
};

export default RouterTabs;
